import { Component, effect, inject, input, model } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { _numberEnumEntries, _stringMapValues, IsoDateTime } from '@naturalcycles/js-lib'
import { countrySharedUtil, GoalBreakpointOption } from '@naturalcycles/shared'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { SharedModule } from '@src/app/shared/shared.module.js'
import { env } from '@src/environments/environment.js'
import { ButtonComponentModule } from '../../button/button.component.module.js'
import { SearchableSelectComponent } from '../../input/searchable-select/searchable-select.component.js'

interface BaseAdminMenuAction<T, P = string> {
  type: T
  payload?: P
}

export type AdminMenuAction =
  | BaseAdminMenuAction<
      | 'restart'
      | 'clearCountry'
      | 'triggerPaymentWithNonce'
      | 'clearExperiments'
      | 'logOutUser'
      | 'toggleInspector'
      | 'resetTime',
      string
    >
  | BaseAdminMenuAction<'changeCountry', { country: string; region: string }>
  | BaseAdminMenuAction<'setExperiment', { experiment?: string; bucket?: string }>
  | BaseAdminMenuAction<'skip', { goal: GoalBreakpointOption }>
  | BaseAdminMenuAction<'timeTravel', { time: IsoDateTime }>

@Component({
  templateUrl: 'admin-menu-modal.component.html',
  selector: 'admin-menu-modal',
  styleUrls: ['admin-menu-modal.component.scss'],
  imports: [ButtonComponentModule, FormsModule, SharedModule, SearchableSelectComponent],
})
export class AdminMenuModalComponent {
  private store = inject(SignalStore)
  paymentNonce = 'fake-valid-nonce'

  // Splits a string on camel case capital letters and capitalizes the first letter of each word
  prettifyLabel = (label: string): string =>
    label
      .split(/(?=[A-Z])/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

  // For use in template
  goalEnum = GoalBreakpointOption
  goals = _numberEnumEntries(GoalBreakpointOption).map(([label, value]) => ({
    label: this.prettifyLabel(label),
    value,
  }))

  protected api = input.required<{ select: (b: AdminMenuAction) => void }>()
  protected isProduction = !!env.prod

  // Countries that always should be displayed at the top of the list
  private topCountries = ['US', 'GB', 'AU', 'CA', 'DE', 'JP', 'BR', 'MX', 'RU']

  countries = _stringMapValues(countrySharedUtil.getAllCountryData())
    .map(c => ({
      label: c.name,
      value: c.code.toUpperCase(),
    }))
    .toSorted((a, b) => {
      if (this.topCountries.includes(a.value)) return -1
      if (this.topCountries.includes(b.value)) return 1
      return a.label.localeCompare(b.label)
    })

  // Override country
  currentCountry: string = localStorage.getItem('e2eCountry') || ''
  selectedCountry: string = this.currentCountry
  currentRegion: string = localStorage.getItem('e2eRegion') || ''
  selectedRegion: string = this.currentRegion

  // Set experiment
  experiment?: string
  bucket?: string

  // Skip quiz
  goal: GoalBreakpointOption = GoalBreakpointOption.prevent

  // Time travel
  overrideTime = this.store.$overrideTime
  overrideTimeFormModel = model(this.overrideTime())

  // Api URL
  environmentApiUrl = env.apiUrl
  apiUrl = this.store.$overrideApiUrl()
  setAdminApiUrl(apiUrl: string | undefined): void {
    this.store.$overrideApiUrl.set(apiUrl)
    this.apiUrl = apiUrl
  }

  onChangeCountry(country: string): void {
    if (country !== 'US') {
      this.selectedRegion = ''
    }
  }

  public getAsIsoDateTime(): IsoDateTime {
    return (this.overrideTimeFormModel() + ':00Z') as IsoDateTime
  }

  constructor() {
    effect(() => {
      this.overrideTimeFormModel.set(this.overrideTime())
    })
  }
}
